// Whether an invitation the API just queued will reach anybody.
//
// Every route that queues a vendor invitation (Send questionnaire, Reassess
// now, Resend, a campaign approval) answers "mail": QUEUED when the worker will
// hand it to a carrier, MUTED when this installation's mail is muted, OFF when
// this workspace switched its mail off, NO_CARRIER when the installation names
// a carrier it does not have, and CARRIER_FAILING when the carrier this
// workspace chose cannot be used as it stands. Only QUEUED may be told as
// sent. When it is not, "reach" says whether the vendor can still answer: the
// portal may send the one time code by SMS or WhatsApp to the number on the
// invitation, and a reminder by phone may carry the link. See
// apps/tprm-api/internal/api/invite_mail.go and packages/core/mailroute.

export type MailState = 'QUEUED' | 'MUTED' | 'OFF' | 'NO_CARRIER' | 'CARRIER_FAILING'

export type Reach = {
  codeBy?: string[]
  reminderBy?: string
  reminderOn?: string
}

export type MailAnswer = {
  mail?: MailState | string
  mailNote?: string
  note?: string
  reach?: Reach
}

// An API older than the field sent QUEUED, or nothing, on every route, and
// that is read the way it was meant.
export function mailWent(r: MailAnswer): boolean {
  return r.mail == null || r.mail === '' || r.mail === 'QUEUED'
}

// The API's own sentence for what became of it, which names the reason and
// the remedy for this installation.
export function mailWords(r: MailAnswer): string {
  return (
    r.mailNote ||
    r.note ||
    "Nothing was emailed. The vendor cannot answer until mail is switched on; then send a reminder from the vendor's next step."
  )
}

// Why nothing went, in a few words, for a heading.
export function whyNotEmailed(mail: string | undefined): string {
  switch (mail) {
    case 'MUTED':
      return 'Mail is muted on this installation.'
    case 'OFF':
      return 'Mail is switched off for this workspace.'
    case 'NO_CARRIER':
      return 'This installation has no mail carrier it can send with.'
    case 'CARRIER_FAILING':
      return "This workspace's mail carrier is switched on and cannot be used as it stands."
    default:
      return 'Mail is not being sent from this workspace.'
  }
}

// Whether an invitation the API reports on was recorded and not emailed: mail
// muted on the installation, or switched off for the workspace. Nothing
// retries those; they need a resend once mail is on. A carrier that fails is
// something else, and is retried.
export function notEmailed(mail: string | undefined | null): boolean {
  return mail === 'MUTED' || mail === 'OFF'
}

// Whether the carrier retries by itself once fixed. A carrier that cannot be
// used fails and is tried again for about a day; muted and switched-off mail
// is recorded and never retried, so it needs a resend.
export function retriesByItself(mail: string | undefined): boolean {
  return mail === 'CARRIER_FAILING'
}

// The first thing to do about it.
export function switchMailOn(r: MailAnswer): string {
  switch (r.mail) {
    case 'MUTED':
      return 'Switch mail on. On your own servers, run the installer again with --mail graph and its four settings (the install guide, section 9, Mail).'
    case 'OFF':
      return 'Switch mail back on for this workspace, in Settings, Platform.'
    case 'NO_CARRIER':
      return 'Give this installation a mail carrier it has. On your own servers, run the installer again with --mail graph (the install guide, section 9, Mail).'
    case 'CARRIER_FAILING':
      return "Fix the mail carrier and test it again, in Settings. The worker keeps trying for about a day and sends the invitation by itself once it works."
    default:
      return mailWords(r)
  }
}

function channelWords(chs: string[] | undefined): string {
  return (chs ?? []).map((c) => (c === 'WHATSAPP' ? 'WhatsApp' : c === 'SMS' ? 'SMS' : c.toLowerCase())).join(' or ')
}

export function reminderDay(iso: string | undefined): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00Z')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
}

// Whether the vendor can still sign in without the email, and how.
export function canStillAnswer(r: MailAnswer): boolean {
  return (r.reach?.codeBy ?? []).length > 0
}

// One sentence on how the vendor can still answer, or that they cannot.
export function answerWords(r: MailAnswer, vendor: string): string {
  const reach = r.reach
  if (canStillAnswer(r)) {
    const link = reach?.reminderBy
      ? 'the reminder that goes by ' + channelWords([reach.reminderBy]) + ' from ' + reminderDay(reach.reminderOn) + ' carries it, or give it to them yourself.'
      : 'nothing will send it to them, so give it to them yourself.'
    return (
      'The questionnaire is recorded, and ' + vendor + ' can still answer it: the portal can send their one time code by ' +
      channelWords(reach?.codeBy) + ' to the mobile number on this invitation. They need the link first: ' + link
    )
  }
  let out =
    'The questionnaire is recorded, but ' + vendor + ' cannot answer it yet: they get neither the invitation nor the one time code they need to sign in.'
  if (reach?.reminderBy) {
    out += ' The reminder that goes by ' + channelWords([reach.reminderBy]) + ' from ' + reminderDay(reach.reminderOn) + ' carries the link, and the code still goes by email.'
  }
  return out
}

// What Send reminder reports, short enough for a toast or a line beside the button.
export function resendSaid(r: MailAnswer & { to: string }): { text: string; tone: 'ok' | 'warn' } {
  if (mailWent(r)) return { text: 'Invitation sent again to ' + r.to, tone: 'ok' }
  let next = 'Nothing reaches the vendor by email until mail is switched on; then send the reminder again.'
  if (retriesByItself(r.mail)) next = 'The worker keeps trying and sends it once the carrier works.'
  if (canStillAnswer(r)) next += ' They can still sign in with a code by ' + channelWords(r.reach?.codeBy) + ' once they have the link.'
  return { text: 'Not sent to ' + r.to + '. ' + whyNotEmailed(r.mail) + ' ' + next, tone: 'warn' }
}
